import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core"
import { Subscription } from "rxjs"
import { MuxDataModel } from "../../models/muxData.model"
import { VideoMedia } from "../../models/resource/media"
import { GTMService } from "../../services/gtm.service"
import { ScriptService } from "../../services/script-service.service"
import { UserAgentService } from "../../services/user-agent.service"
import PlayerUtils from "../../utils/player-utils"
import { GtmVideoJsInfo } from "../../utils/types"
import { ResourceService } from "../../services/resource.service"

declare const videojs: unknown | any

/*
Based on https://docs.videojs.com/tutorial-angular.html.
Duplicated in the most part for audio.component.ts. Please consider this when making changes.
 */
@Component({
    selector: "app-video",
    templateUrl: "./video.component.html",
    styleUrls: ["./video.component.scss"],
    // DOM elements added by video.js do not have Angular namespaces and break, so disable this
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class VideoComponent implements AfterViewInit, OnDestroy {
    @ViewChild("player") target: ElementRef

    @Input() containerId: string = "videoContainer-" + Date.now()
    @Input() muxData: MuxDataModel = new MuxDataModel()
    @Input() gtmVideoJsInfo: GtmVideoJsInfo
    @Input() cautionFlagButtonName: string = "Watch Video"
    @Input() isCautionFlagContainerDisplayed: boolean = false
    @Input() customClasses: string = ""

    @Input() set videoMedia(value: VideoMedia) {
        // Note: maybe the onChanges handling in audio is more efficient if this does multiple updatePlayer calls
        this.video = value
        this.crawlerSafeVideoSrc = value.backup ? value.backup.getURL() : value.usedFile.getURL()
        if (!this.video.isGeoRestrictionEvaluated()) {
            this.subscriptions.push(
                this.video.geoRestrictionEvaluated.subscribe((evaluatedMedia) => {
                    if (evaluatedMedia !== this.video) {
                        return
                    }
                    this.updatePlayer()
                })
            )
        } else {
            this.updatePlayer()
        }
    }

    public player: any = null
    public subscriptions: Subscription[] = []
    public crawlerSafeVideoSrc: string

    public video: VideoMedia
    private viewInit: boolean = false
    private firstInitialize: boolean = false
    overrideNativeVhs =
        navigator.vendor &&
        navigator.vendor.indexOf("Apple") > -1 &&
        navigator.userAgent &&
        navigator.userAgent.indexOf("CriOS") == -1 &&
        navigator.userAgent.indexOf("FxiOS") == -1

    constructor(
        public userAgentService: UserAgentService,
        private scriptService: ScriptService,
        private gtmService: GTMService,
        private resourceService: ResourceService
    ) {
        this.viewInit = false
    }

    public ngAfterViewInit() {
        this.viewInit = true
        this.updatePlayer()
    }

    ngOnChanges(changes: SimpleChanges) {
        // check done in order to determine if changes were done on content box,
        //  if true, show/hide progress bar and play button
        if (this.isFirstCautionFlagDisplayChanges(changes)) {
            this.toggleControlBar()
        }
    }

    isFirstCautionFlagDisplayChanges(changes: any) {
        return (
            !!changes.isCautionFlagContainerDisplayed?.currentValue !==
            !!changes.isCautionFlagContainerDisplayed?.previousValue
        )
    }

    playVideo() {
        if (!this.player) return
        this.player.play()
        this.togglePlayButton("none")
    }

    toggleControlBar() {
        let playerContainer: HTMLElement = document
            .getElementById("pbslm-video-player")
            ?.querySelector(".vjs-control-bar") as HTMLElement
        if (!playerContainer) return
        if (this.isCautionFlagContainerDisplayed) {
            playerContainer.style.display = "none"
            this.togglePlayButton("none")
        } else {
            playerContainer.style.display = "flex"
            if (this.player.paused) this.togglePlayButton("flex")
        }
    }

    private togglePlayButton(displayState: string) {
        let playerPlayButton: HTMLElement = document
            .getElementById("pbslm-video-player")
            ?.querySelector(".vjs-big-play-button") as HTMLElement
        if (!playerPlayButton) return
        playerPlayButton.style.display = displayState
    }

    public ngOnDestroy() {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe())
        if (this.player) {
            // https://docs.videojs.com/tutorial-player-workflows.html#dispose
            // When the component is destroyed we still want to clean the DOM and the memory
            this.player.dispose()
        }
    }

    private updatePlayer() {
        if (
            !this.video ||
            !this.viewInit ||
            !this.video.isGeoRestrictionEvaluated() ||
            this.userAgentService.isGoogleCrawler
        ) {
            return
        }

        this.scriptService.load("videojs", "videojs.css", "videojs-seek-chapter.css").then(() => {
            const videoPlugins = [
                "videojs-mux",
                "videojs-http-source-selector",
                "videojs-seek-buttons",
                "videojs-errors",
                "videojs-hotkeys",
                "videojs-contrib-quality-levels",
                "videojs-seek-chapter",
            ]
            // Plugins must be loaded after video.js is loaded in order to be registered.
            this.scriptService.load(...videoPlugins).then(() => {
                this.initVideoJS()
            })
        })
    }

    private initVideoJS() {
        /*
        We need to account that the component will be kept and the VideoMedia will change (gallery example).
        So instantiate the player only once and assume it needs cleaning.
        https://docs.videojs.com/player#reset preferred, but this would also work:
        this.player.remoteTextTracks().tracks_.forEach((track) => {
            this.player.removeRemoteTextTrack(track);
        });
         */
        if (!this.player) {
            const hotKeysSettings = {
                seekStep: 10,
                enableVolumeScroll: false,
            }

            // First instantiation. Not setting src/cc/poster options here because it would prevent it from being reset
            this.player = videojs(this.target.nativeElement, {
                playbackRates: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
                html5: {
                    ...(videojs.browser.IS_ANY_SAFARI ? { nativeTextTracks: false } : {}),
                    vhs: {
                        overrideNative: this.overrideNativeVhs,
                        enableLowInitialPlaylist: false,
                        fastQualityChange: true,
                        useDtsForTimestampOffset: true,
                    },
                    nativeTextTracks: false,
                },
                controlBar: {
                    volumePanel: { inline: false },
                },
                plugins: {
                    seekChapter: {
                        chapterDisplayLength: 3,
                    },
                },
            })
            const tech = this.player.tech()
            tech.on("usage", (event) => {
                if (event.name === "hls-gap-skip") {
                    this.logGapSkip()
                }
            })
            if (this.player.seekButtons) {
                this.player.seekButtons({ back: 10 })
            }
            if (this.player.httpSourceSelector) {
                this.player.httpSourceSelector()
            }
            if (this.player.errors) {
                this.player.errors({
                    dismiss: false,
                    errors: PlayerUtils.ErrorOverrides,
                })
            }
            this.player.controlBar.removeChild("pictureInPictureToggle")
            this.player.on("play", (e) => this.togglePlayButton("none"))
            this.player.on("touchstart", (e) => {
                if (e.target.nodeName === "VIDEO") {
                    if (this.player.paused()) {
                        this.player.play()
                    } else {
                        this.player.pause()
                    }
                }
            })
            PlayerUtils.finalizePlayerSetup(
                this.player,
                this.gtmService,
                this.gtmVideoJsInfo,
                hotKeysSettings
            )
            this.firstInitialize = true
        } else {
            // Moved to a different video media in the gallery
            this.firstInitialize = false
            this.player.pause() // Without this, the reset is causing a loading state
            this.player.reset()
        }
        this.updateVideoJS()
    }

    private updateVideoJS() {
        if (this.video.isAvailableToUser()) {
            this.player.poster(this.video.getDefaultPosterImage())
            this.player.src(this.video.getVideoJsSources())
            if (this.video.is360) {
                this.scriptService.load("videojs-vr").then(() => {
                    this.player.vr({ projection: "360" })
                })
            }

            PlayerUtils.loadCaptionLanguage(this.player, this.video)
            PlayerUtils.trackCaptionSettingsState(this.player)
            this.toggleControlBar()
        } else {
            this.togglePlayButton("none")
            this.player.error({ code: "PLAYER_ERR_GEO_RESTRICTED" })
        }
        this.resourceService.updateGeoRestrictedState = !this.video.isAvailableToUser()
        this.muxData.setupForVideoJS(this.player, this.firstInitialize)
        this.gtmService.setMediaType(this.gtmVideoJsInfo && this.gtmVideoJsInfo.type)
        this.gtmService.setVideoInfo(`${this.gtmVideoJsInfo.title} | ${this.gtmVideoJsInfo.guid}`)
    }

    private logGapSkip() {
        if (this.player.qualityLevels) {
            const qualityLevels = this.player.qualityLevels()
            const quality = qualityLevels[qualityLevels.selectedIndex]
            const data = `${this.muxData.title} ${quality.label} ${quality.width} ${quality.height} ${quality.bitrate}`
            this.gtmService.setVideoData(data)
        }
        this.gtmService.pushEvent("hls-gap-skip")
    }
}
